import React, { useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useOS, MENU_BAR_HEIGHT, DOCK_HEIGHT } from './OSContext';
import { getApp } from './apps';

const MIN_VISIBLE = 80; // px of titlebar that must stay reachable on screen

function clamp(n, min, max) {
	return Math.min(Math.max(n, min), max);
}

const Window = ({ appId, isMobile }) => {
	const { windows, order, activeId, closeWindow, focusWindow, minimizeWindow, toggleMaximize, moveWindow, resizeWindow } =
		useOS();
	const win = windows[appId];
	const app = getApp(appId);
	const dragState = useRef(null);
	const resizeState = useRef(null);

	const isActive = activeId === appId;
	const AppComponent = app?.component;

	const handleTitlePointerDown = useCallback(
		e => {
			if (!win || win.maximized || isMobile) return;
			focusWindow(appId);
			dragState.current = { startX: e.clientX, startY: e.clientY, winX: win.x, winY: win.y };
			const handleMove = ev => {
				if (!dragState.current) return;
				const dx = ev.clientX - dragState.current.startX;
				const dy = ev.clientY - dragState.current.startY;
				const nextX = clamp(dragState.current.winX + dx, -(win.width - MIN_VISIBLE), window.innerWidth - MIN_VISIBLE);
				const nextY = clamp(dragState.current.winY + dy, MENU_BAR_HEIGHT, window.innerHeight - 32);
				moveWindow(appId, nextX, nextY);
			};
			const handleUp = () => {
				dragState.current = null;
				window.removeEventListener('pointermove', handleMove);
				window.removeEventListener('pointerup', handleUp);
			};
			window.addEventListener('pointermove', handleMove);
			window.addEventListener('pointerup', handleUp);
		},
		[win, appId, isMobile, focusWindow, moveWindow]
	);

	const startResize = useCallback(
		(edge, e) => {
			if (!win || win.maximized || isMobile) return;
			e.stopPropagation();
			focusWindow(appId);
			resizeState.current = {
				startX: e.clientX,
				startY: e.clientY,
				width: win.width,
				height: win.height,
				x: win.x,
				y: win.y,
			};
			const minW = app?.minSize?.width || 280;
			const minH = app?.minSize?.height || 200;
			const handleMove = ev => {
				if (!resizeState.current) return;
				const dx = ev.clientX - resizeState.current.startX;
				const dy = ev.clientY - resizeState.current.startY;
				const bounds = {};
				if (edge === 'right' || edge === 'corner') {
					bounds.width = clamp(resizeState.current.width + dx, minW, window.innerWidth - resizeState.current.x);
				}
				if (edge === 'bottom' || edge === 'corner') {
					bounds.height = clamp(resizeState.current.height + dy, minH, window.innerHeight - resizeState.current.y);
				}
				resizeWindow(appId, bounds);
			};
			const handleUp = () => {
				resizeState.current = null;
				window.removeEventListener('pointermove', handleMove);
				window.removeEventListener('pointerup', handleUp);
			};
			window.addEventListener('pointermove', handleMove);
			window.addEventListener('pointerup', handleUp);
		},
		[win, app, appId, isMobile, focusWindow, resizeWindow]
	);

	if (!win || !app) return null;

	const zIndex = 10 + order.indexOf(appId);
	const style = isMobile
		? {
				left: 0,
				top: MENU_BAR_HEIGHT,
				width: '100vw',
				height: `calc(100vh - ${MENU_BAR_HEIGHT}px - ${DOCK_HEIGHT}px)`,
				zIndex,
			}
		: { left: win.x, top: win.y, width: win.width, height: win.height, zIndex };

	return (
		<motion.div
			role="dialog"
			aria-label={app.title}
			initial={{ opacity: 0, scale: 0.96 }}
			animate={{
				opacity: win.minimized ? 0 : 1,
				scale: win.minimized ? 0.05 : 1,
				x: win.minimized ? -(win.x - window.innerWidth / 2) : 0,
				y: win.minimized ? window.innerHeight - win.y : 0,
				pointerEvents: win.minimized ? 'none' : 'auto',
			}}
			exit={{ opacity: 0, scale: 0.95 }}
			transition={{ type: 'spring', stiffness: 300, damping: 28 }}
			style={{ position: 'fixed', ...style }}
			onPointerDownCapture={() => !isActive && focusWindow(appId)}
			className={`flex flex-col rounded-xl overflow-hidden border shadow-2xl bg-os-surface font-os ${
				isActive ? 'border-white/20 shadow-[0_24px_60px_rgba(0,0,0,0.55)]' : 'border-white/10 shadow-[0_12px_30px_rgba(0,0,0,0.4)]'
			}`}
		>
			{/* Title bar */}
			<div
				onPointerDown={handleTitlePointerDown}
				onDoubleClick={() => !isMobile && toggleMaximize(appId)}
				className={`group flex items-center h-9 shrink-0 px-3 select-none bg-os-chrome border-b border-black/30 ${
					isMobile ? '' : 'cursor-grab active:cursor-grabbing'
				}`}
			>
				<div className="flex items-center gap-2">
					<button
						aria-label="Close"
						onClick={e => {
							e.stopPropagation();
							closeWindow(appId);
						}}
						className="w-3 h-3 rounded-full bg-os-red flex items-center justify-center text-[7px] text-black/60 leading-none"
					>
						<span className="opacity-0 group-hover:opacity-100">✕</span>
					</button>
					<button
						aria-label="Minimize"
						onClick={e => {
							e.stopPropagation();
							minimizeWindow(appId);
						}}
						disabled={isMobile}
						className="w-3 h-3 rounded-full bg-os-yellow flex items-center justify-center text-[7px] text-black/60 leading-none disabled:opacity-40"
					>
						<span className="opacity-0 group-hover:opacity-100">−</span>
					</button>
					<button
						aria-label="Maximize"
						onClick={e => {
							e.stopPropagation();
							!isMobile && toggleMaximize(appId);
						}}
						disabled={isMobile}
						className="w-3 h-3 rounded-full bg-os-green flex items-center justify-center text-[7px] text-black/60 leading-none disabled:opacity-40"
					>
						<span className="opacity-0 group-hover:opacity-100">⤢</span>
					</button>
				</div>
				<div className="flex-1 text-center text-xs text-os-textdim font-medium pr-12 truncate">{app.title}</div>
			</div>

			{/* Content */}
			<div className="flex-1 min-h-0 overflow-auto bg-os-surface text-os-text">
				{AppComponent ? <AppComponent /> : null}
			</div>

			{/* Resize handles (desktop only) */}
			{!isMobile && !win.maximized && (
				<>
					<div
						onPointerDown={e => startResize('right', e)}
						className="absolute top-0 right-0 w-1.5 h-full cursor-ew-resize"
					/>
					<div
						onPointerDown={e => startResize('bottom', e)}
						className="absolute bottom-0 left-0 w-full h-1.5 cursor-ns-resize"
					/>
					<div
						onPointerDown={e => startResize('corner', e)}
						className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize"
					/>
				</>
			)}
		</motion.div>
	);
};

export default Window;
